import { useState, useEffect, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';
import _ from 'lodash';
import { Auth, recordActions, recordManager } from '../../config/configuration/react';
import { autobizMarketUrl, RECORD_STATUS } from '../../config';
import { numStr } from '../../components/common/Translate';
import { historyPresenter } from '../../Utils/presenters/vincheck/historyPresenter';
import { Record } from '../../config/Record/RecordEntity';
import { getQuotationbyVersionId } from '../../services/SearchVehicle/getVehicleData';
import { extractVehData } from '../../Store/helpers/pricing';

export const usePricingTab = () => {
  const { recordId } = useParams();
  const dispatch = useDispatch();
  const [mileage, setMileage] = useState('');
  const [version, setVersion] = useState(null);
  const [modal, setModal] = useState(false);
  const [alertModal, setAlertModal] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [usedImmatRecords, setUsedImmatRecords] = useState(null);
  const [LicensePlateAlertNumber, setLicensePlateAlertNumber] = useState(null);
  const toggle = () => setModal(!modal);
  const toggleAlert = () => setAlertModal(!alertModal);
  const cancelAlert = () => setAlertModal(false);
  const appLang = Auth.preferredLanguage();
  const dateFormat = appLang === 'DE' ? 'DD.MM.YYYY HH:mm' : 'DD/MM/YYYY HH:mm';

  const record = useSelector((state) => state.reduxRecords.data[recordId]);
  const recordEntity = new Record(record);
  const versionsAvailable = useSelector((state) => state.reduxRecords.data[recordId]?.vehicle?.versionsData) || [];
  const [vinVersions, setVinVersions] = useState(null);
  const changeVersion = useRef(null);
  const [vinVersionsLoading, setVinVersionsLoading] = useState(false);
  const vinVersionSupplier = useSelector((state) => state.reduxRecords.data[recordId]?.vehicle?.versionSupplier);
  const vinVersionOrg = useSelector((state) => state.reduxRecords.data[recordId]?.vehicle?.versionOrg);
  const [versionOrg, setVersionOrg] = useState(vinVersionOrg);
  const [versionSupplier, setVersionSupplier] = useState(vinVersionSupplier);
  const valuationVersionId = useSelector((state) => state.reduxRecords.data[recordId]?.valuation?.versionId);
  const vehicleVersionId = useSelector((state) => state.reduxRecords.data[recordId]?.vehicle?.versionId);
  const recordMileage = useSelector((state) => state.reduxRecords.data[recordId]?.vehicle?.mileage);
  const versionType = useSelector((state) => state.reduxRecords.data[recordId]?.vehicle?.versionType) || '';
  const carEquipments = useSelector((state) => state.reduxRecords.data[recordId]?.vehicle?.carEquipment) || [];
  const [carEquipment, setCarEquipment] = useState(carEquipments);
  const buyerVersionList = useSelector((state) => state.reduxRecords.data[recordId]?.vehicle?.buyerVersionList) || '';
  const recordStatus = useSelector((state) => state.reduxRecords.data[recordId]?.record?.statusId);
  let currentVersion = version || valuationVersionId || vehicleVersionId;
  const historysList = useSelector((state) => state.reduxRecords.data[recordId]?.history || []);
  const historys = historyPresenter(historysList);
  const standardMileage = useSelector((state) => state.reduxRecords.data[recordId])?.quotation?.standardMileage || null;
  const userPricing = useSelector((state) => state.reduxRecords.data[recordId]?.userPricing || {});
  const vinPage = useSelector((state) => state.reduxRecords.data[recordId]?.sourceInfos?.sourceGroupData?.vinPage);
  const role = Auth.role();
  const concessionIds = Auth.concessionIds();
  const {
    expertise_finished,
    expertise_authorized,
    supervisor_refused,
    refused,
    report,
    repurchase_account,
    counter_appraisal_verification,
  } = RECORD_STATUS;

  let versions = versionsAvailable.map((v, key) => {
    if (!currentVersion && key === 0) {
      currentVersion = v.id;
      return { ...v, selected: true };
    }
    return {
      ...v,
      selected: v.id === currentVersion,
    };
  });
  useEffect(() => {
    if (changeVersion.current && versionType !== 'vin' && vinVersions) {
      setVinVersions(
        vinVersions.map((v, key) => {
          if (!currentVersion && key === 0) {
            currentVersion = v.id;
            return { ...v, selected: true };
          }
          return {
            ...v,
            selected: v.id === currentVersion,
          };
        }),
      );
    }
  }, [currentVersion]);

  const getVersions = async () => {
    setIsLoading(true);
    await dispatch(recordActions.getVehicleVersions(recordId));
    setIsLoading(false);
  };

  const checkUsedImmat = async () => {
    setIsLoading(true);
    try {
      const query = {
        registration: recordEntity.getRegistration(),
        status: [expertise_finished, expertise_authorized, supervisor_refused, refused, report, repurchase_account],
        range: [0, 9],
        sourceIds: Auth.sourceIds().join(','),
        verifAccess: 'false',
        recordTypeId: 1,
        groupId: Auth.groupId(),
      };

      let { records } = await recordManager.search(query);
      records = records.filter((obj) => {
        return obj.id !== parseInt(recordId, 10);
      });
      setIsLoading(false);
      return records;
    } catch (error) {
      setIsLoading(false);
      throw new Error('empty datas');
    }
  };

  useEffect(() => {
    if (mileage) setMileage(numStr(mileage));
    else setMileage(numStr(recordMileage));
    getVersions();
  }, []);

  useEffect(() => {
    if (recordStatus >= 3) dispatch(recordActions.fetchPricingHistory(recordId));
  }, [recordStatus]);

  useEffect(() => {
    checkUsedImmat().then((r) => {
      setUsedImmatRecords(r);
      setLicensePlateAlertNumber(r.length);
    });
  }, [recordId]);

  const refresh = async () => {
    setIsLoading(true);
    await dispatch(
      recordActions.updatePricingVehicle({
        recordId,
        mileage: (mileage && parseInt(mileage.replace(/[^0-9]/g, ''), 10)) || recordMileage,
        versionId: parseInt(currentVersion, 10),
        versionName: _.find(
          changeVersion.current && vinVersions ? vinVersions : versionsAvailable,
          (v) => v.id === currentVersion,
        ).name,
        versionData:
          changeVersion.current && vinVersions
            ? vinVersions.map((item) => {
                const { quotation, ...res } = item;
                return res;
              })
            : null,
        equipments: carEquipment,
      }),
    );
    await dispatch(
      recordActions.fetchPricing(
        recordId,
        userPricing.plannedDelay,
        userPricing.marketValue,
        parseInt(userPricing.refurbishingCosts, 10),
        currentVersion,
      ),
    );
    // Need to check if the USER and the FILE are from NL
    // await dispatch(
    //   recordActions.getAutotelex(recordEntity.getRegistration(), {
    //     mileage: (mileage && parseInt(mileage.replace(/[^0-9]/g, ''), 10)) || recordMileage,
    //     versionId: parseInt(currentVersion, 10),
    //   }),
    // );
    setIsLoading(false);
  };

  const country = Auth.country()?.toLowerCase();
  const Name = country === 'ES' ? 'informe_estadoVO' : 'rapport_d_inspection';
  const linkVinPage = `${autobizMarketUrl}/${country}/SVO/vin_superviseur.php?id=${recordId}`;

  const formatValue = (v) => {
    let value = v;
    value = value.replace(/[^0-9]/g, '');
    if (value !== '') {
      if (value.length > 6) return;
      value = numStr(value);
      setMileage(value);
    } else setMileage('');
  };

  const onChange = (e) => {
    const { id, value } = e.target;
    if (id === 'versionId') setVersion(parseInt(value, 10));
    if (id === 'mileage') formatValue(value);
  };

  const getVehicleVersionsByDecodeVin = async () => {
    setVinVersionsLoading(true);
    try {
      const mileAge = (mileage && parseInt(mileage.replace(/[^0-9]/g, ''), 10)) || recordMileage;
      const { yearMec } = extractVehData(record);
      const data = await dispatch(recordActions.getVehicleVersionsByVin(recordId));
      if (data?.versions) {
        setVersionSupplier(data?._meta?.supplier || null);
        setVersionOrg(data?.orgVersion || null);
        setCarEquipment(data.equipments?.filter((eqp) => eqp.id) || []);
        const newData = await Promise.all(
          data.versions.slice(-10).map(async (v) => {
            try {
              const quotation = await getQuotationbyVersionId(v.id, mileAge, yearMec);
              return { ...v, quotation: quotation || null };
            } catch (error) {
              return v;
            }
          }),
        );
        setVinVersions(newData);
      }
    } catch (error) {
      setVinVersions(null);
    } finally {
      setVinVersionsLoading(false);
    }
  };

  const checkedVinVersions = (e) => {
    setVersion(parseInt(e.target.value, 10));
    if (!changeVersion.current) changeVersion.current = true;
    if (versionType === 'vin') {
      versions = versions.map((v) =>
        v.id.toString() === e.target.value.toString() ? { ...v, selected: true } : { ...v, selected: false },
      );
    } else {
      setVinVersions(
        vinVersions.map((v) =>
          v.id.toString() === e.target.value.toString() ? { ...v, selected: true } : { ...v, selected: false },
        ),
      );
    }
  };

  return {
    versions,
    mileage,
    recordId,
    onChange,
    refresh: version || mileage ? refresh : null,
    readOnly: recordEntity.readOnlyStatus(),
    linkVinPage,
    isLoading,
    showHistory: historys.length > 0,
    historys,
    toggle,
    modal,
    dateFormat,
    standardMileage,
    vinPage,
    role,
    concessionIds,
    usedImmatRecords,
    LicensePlateAlertNumber,
    alertModal,
    toggleAlert,
    cancelAlert,
    Name,
    recordCountryId: record?.record?.countryId,
    CounterAppraisalAnalys: !recordEntity.isNrmRecord() && recordEntity.status() === counter_appraisal_verification,
    getVehicleVersionsByDecodeVin,
    vinVersions,
    vinVersionsLoading,
    checkedVinVersions,
    versionType,
    changeVersion,
    currentVersion,
    versionSupplier,
    versionOrg,
    buyerVersionList,
    carEquipment,
  };
};
